import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { AutomationForgeElementType } from "@redotech/redo-model/forge/marketing-automation-forge-types";
import { toast } from "@redotech/redo-web/alert";
import { BaseReactFlow } from "@redotech/redo-web/automations/flow";
import { Flex } from "@redotech/redo-web/flex";
import "@xyflow/react/dist/style.css";
import { Dispatch, memo, SetStateAction, useMemo } from "react";
import { DropEdge } from "./edges/drop-edge";
import { CustomEdge } from "./edges/edge";
import { LabeledEdge } from "./edges/labeled-edge";
import { buildNodesAndEdges, NodeTypes } from "./flow-converter";
import { FlowInProgress } from "./flow-editor";
import "./flow.css";
import { AbTestNode } from "./nodes/ab-test-node";
import { BlockCheckoutNode } from "./nodes/block-checkout-node";
import { BranchNode } from "./nodes/branch-node";
import { CheckoutExperienceNode } from "./nodes/checkout-experience-node";
import { ConciergeBlockNode } from "./nodes/concierge-block-node";
import { CustomerTagActionNode } from "./nodes/customer-tag-action-node";
import { DownsellNode } from "./nodes/downsell-node";
import { DropZoneNode } from "./nodes/drop-zone-node";
import { EndNode } from "./nodes/end-node";
import { ManageStaticSegmentNode } from "./nodes/manage-static-segment-node";
import { OmsActionNode } from "./nodes/oms/oms-action-node";
import { OmsConditionNode } from "./nodes/oms/oms-condition-node";
import { PostPurchasePageNode } from "./nodes/post-purchase-page-node";
import { PostPurchaseSegmentNode } from "./nodes/post-purchase-segment-node";
import { PostPurchaseSplitNode } from "./nodes/post-purchase-split-node";
import { ProcessReturnNode } from "./nodes/process-return-node";
import { RcoCheckoutPackageConditionNode } from "./nodes/rco-checkout-package-condition-node";
import { RcoCheckoutPackageNode } from "./nodes/rco-checkout-package-node";
import { RcoSegmentNode } from "./nodes/rco-segment-node";
import { RcoSplitNode } from "./nodes/rco-split-node";
import { RecoverNode } from "./nodes/recover-node";
import { RuleActionsNode } from "./nodes/rules/rule-actions-node";
import { RuleConditionsNode } from "./nodes/rules/rule-conditions-node";
import { SendMessageNode } from "./nodes/send-message-node";
import { SendWebhookNode } from "./nodes/send-webhook-node";
import { TrackingPageNode } from "./nodes/tracking-page-node";
import { TriggerNode } from "./nodes/trigger-node";
import { UpsellConditionNode } from "./nodes/upsell-condition-node";
import { UpsellNode } from "./nodes/upsell-node";
import { WaitForEventNode } from "./nodes/wait-for-event-node";
import { WaitNode } from "./nodes/wait-node";
import { WorkOrderConditionNode } from "./nodes/work-order/work-order-condition-node";
import { WorkOrderWorkStepNode } from "./nodes/work-order/work-order-work-step-node";
import { PartialFlowStep } from "./steps";
import { deleteElement } from "./utils/delete-element";
import { dropElement } from "./utils/drop-element";

interface CommonFlowChartProps {
  automation: FlowInProgress;
  selected: string | undefined;
  onNodeClick: (nodeId: string | undefined) => void;
  schema: Schema;
  partialFlow?: FlowInProgress;
  elementDrop?: ElementDrop | undefined;
  deleteElementOverride?:
    | ((id: string, steps: PartialFlowStep[]) => PartialFlowStep[])
    | undefined;
  showMiniMap?: boolean;
  hoveredNodeId?: string | null;
}

export type FlowChartProps = (
  | { readonly: true; setAutomation?: undefined; setModified?: undefined }
  | {
      readonly: false;
      setAutomation: Dispatch<SetStateAction<FlowInProgress | null>>;
      setModified: Dispatch<SetStateAction<boolean>>;
    }
) &
  CommonFlowChartProps;

export const Flowchart = memo(function Flowchart({
  automation,
  selected,
  onNodeClick,
  schema,
  partialFlow,
  elementDrop,
  deleteElementOverride,
  showMiniMap,
  hoveredNodeId,
  ...rest
}: FlowChartProps) {
  const nodeTypes: Record<NodeTypes, any> = {
    [NodeTypes.TRIGGER]: TriggerNode,
    [NodeTypes.WAIT]: WaitNode,
    [NodeTypes.WAIT_FOR_EVENT]: WaitForEventNode,
    [NodeTypes.BRANCH]: BranchNode,
    [NodeTypes.SEND_MESSAGE]: SendMessageNode,
    [NodeTypes.SEND_WEBHOOK]: SendWebhookNode,
    [NodeTypes.CUSTOMER_TAG_ACTION]: CustomerTagActionNode,
    [NodeTypes.MANAGE_STATIC_SEGMENT]: ManageStaticSegmentNode,
    [NodeTypes.RECOVER]: RecoverNode,
    [NodeTypes.END]: EndNode,
    [NodeTypes.DROP_ZONE]: DropZoneNode,
    [NodeTypes.UPSELL]: UpsellNode,
    [NodeTypes.DOWNSELL]: DownsellNode,
    [NodeTypes.UPSELL_CONDITION]: UpsellConditionNode,
    [NodeTypes.OMS_ACTION]: OmsActionNode,
    [NodeTypes.OMS_CONDITION]: OmsConditionNode,
    [NodeTypes.RCO_SPLIT]: RcoSplitNode,
    [NodeTypes.CHECKOUT_EXPERIENCE]: CheckoutExperienceNode,
    [NodeTypes.BLOCK_CHECKOUT]: BlockCheckoutNode,
    [NodeTypes.RCO_SEGMENT]: RcoSegmentNode,
    [NodeTypes.RCO_CHECKOUT_PACKAGE]: RcoCheckoutPackageNode,
    [NodeTypes.RCO_CHECKOUT_PACKAGE_CONDITION]: RcoCheckoutPackageConditionNode,
    [NodeTypes.AB_TEST]: AbTestNode,
    [NodeTypes.POST_PURCHASE_PAGE]: PostPurchasePageNode,
    [NodeTypes.POST_PURCHASE_SEGMENT]: PostPurchaseSegmentNode,
    [NodeTypes.POST_PURCHASE_SPLIT]: PostPurchaseSplitNode,
    [NodeTypes.TRACKING_PAGE]: TrackingPageNode,
    [NodeTypes.RULE_CONDITIONS]: RuleConditionsNode,
    [NodeTypes.RULE_ACTIONS]: RuleActionsNode,
    [NodeTypes.WORK_ORDER_WORK_STEP]: WorkOrderWorkStepNode,
    [NodeTypes.WORK_ORDER_CONDITION]: WorkOrderConditionNode,
    [NodeTypes.PROCESS_RETURN]: ProcessReturnNode,
    [NodeTypes.CONCIERGE_BLOCK]: ConciergeBlockNode,
  };

  const edgeTypes = {
    edge: CustomEdge,
    labeled: LabeledEdge,
    dropEdge: DropEdge,
  };

  const elements = useMemo(
    () =>
      buildNodesAndEdges({
        automation: partialFlow ?? automation,
        elementDropped: (elementType, idFrom, idTo, conditionSide, edgeId) => {
          if (rest.readonly) {
            return;
          }

          const elementDropAllowed = elementDrop?.isElementDropAllowed({
            elementType,
            idFrom,
            idTo,
            flowInProgress: automation,
          }) ?? { isAllowed: true };
          if (elementDropAllowed.isAllowed) {
            const updatedSteps = dropElement({
              elementType,
              idFrom,
              idTo,
              conditionSide,
              edgeId,
              automation,
              schema,
              setSelected: onNodeClick,
            });

            rest.setAutomation({ ...automation, steps: updatedSteps });
            rest.setModified(true);
          } else {
            toast(elementDropAllowed.errorMessage, { variant: "error" });
          }
        },
        deleteElements: (id: string) => {
          if (rest.readonly) {
            return;
          }

          const newSteps = deleteElementOverride
            ? deleteElementOverride(id, automation.steps)
            : deleteElement(id, automation.steps);
          rest.setAutomation({ ...automation, steps: newSteps });
          rest.setModified(true);
        },
        updateStep: (id: string, updatedStep: PartialFlowStep) => {
          if (rest.readonly) {
            return;
          }

          const newSteps = automation.steps.map((step) =>
            step.id === id ? updatedStep : step,
          );
          rest.setAutomation({ ...automation, steps: newSteps });
          rest.setModified(true);
        },
        selected,
        readonly: rest.readonly,
        hoveredNodeId,
      }),
    [
      partialFlow,
      automation,
      schema,
      selected,
      onNodeClick,
      elementDrop,
      deleteElementOverride,
      rest,
      hoveredNodeId,
    ],
  );

  return (
    <Flex
      dir="column"
      flex={3.5}
      gap="none"
      height="full"
      position="relative"
      w="full"
    >
      <BaseReactFlow
        edgeTypes={edgeTypes}
        initialEdges={elements.edges}
        initialNodes={elements.nodes}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        showMiniMap={showMiniMap}
      />
    </Flex>
  );
});

export interface ElementDrop {
  isElementDropAllowed: (input: {
    elementType: AutomationForgeElementType;
    idFrom: string;
    idTo?: string;
    flowInProgress: FlowInProgress;
  }) => { isAllowed: true } | { isAllowed: false; errorMessage: string };
}
